// client/src/components/sms/SmsActionPage.js
import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import ApiSmsActions from '../../api/ApiSmsActions'

const redeemRequestCache = new Map()

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
}

const ERROR_MESSAGES = {
  TOKEN_NOT_FOUND: 'This action link is invalid.',
  TOKEN_WRONG_USER: 'This link belongs to a different account.',
  TOKEN_EXPIRED: 'This action link has expired.',
  TOKEN_ALREADY_USED: 'This action has already been performed.',
  PROPOSAL_EXPIRED: 'This proposal has already expired.',
  MATCH_CONFLICT: 'Unable to accept — you already have a match at that time.',
  REQUEST_FAILED: 'Unable to complete this request right now.',
}

function getErrorMessage(errorCode, fallbackMessage) {
  return ERROR_MESSAGES[errorCode] || fallbackMessage || 'Something went wrong.'
}

function redeemTokenOnce(token) {
  if (!redeemRequestCache.has(token)) {
    const request = ApiSmsActions.redeem(token).catch((error) => {
      redeemRequestCache.delete(token)
      throw error
    })

    redeemRequestCache.set(token, request)
  }

  return redeemRequestCache.get(token)
}

export function __resetSmsActionRedeemRequestCache() {
  redeemRequestCache.clear()
}

function LoadingState() {
  return (
    <div className="text-center py-5" data-testid="sms-action-loading">
      <div className="spinner-border text-primary mb-3" role="status">
        <span className="visually-hidden">Loading…</span>
      </div>
      <p className="text-muted">Processing your request…</p>
    </div>
  )
}

function SuccessState({ actionType, matchId }) {
  const verb = actionType === 'accept' ? 'accepted' : 'declined'
  const label = actionType === 'accept' ? 'Accepted' : 'Declined'

  return (
    <div className="text-center py-5" data-testid="sms-action-success">
      <div
        className={`mb-3 fs-1 ${actionType === 'accept' ? 'text-success' : 'text-secondary'}`}
        aria-hidden="true"
      >
        {actionType === 'accept' ? '✓' : '✗'}
      </div>
      <h2 className="h4 mb-2">Proposal {label}</h2>
      <p className="text-muted mb-4">
        You have {verb} the match proposal.
        {actionType === 'accept' &&
          matchId &&
          ' Your match has been scheduled.'}
      </p>
      <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
        {matchId && actionType === 'accept' && (
          <Link
            to={`/leagues/matches/${matchId}`}
            className="btn btn-primary"
            data-testid="sms-action-view-match"
          >
            View Match
          </Link>
        )}
        <Link
          to="/match-proposals"
          className="btn btn-outline-secondary"
          data-testid="sms-action-view-proposals"
        >
          All Proposals
        </Link>
      </div>
    </div>
  )
}

function ErrorState({ message }) {
  return (
    <div className="text-center py-5" data-testid="sms-action-error">
      <div className="mb-3 fs-1 text-warning" aria-hidden="true">
        ⚠
      </div>
      <h2 className="h4 mb-2">Unable to Complete Action</h2>
      <p className="text-muted mb-4" data-testid="sms-action-error-message">
        {message}
      </p>
      <Link
        to="/match-proposals"
        className="btn btn-outline-primary"
        data-testid="sms-action-view-proposals"
      >
        View Proposals
      </Link>
    </div>
  )
}

function SmsActionPage() {
  const { token } = useParams()
  const { isLoggedIn, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [status, setStatus] = useState(STATUS.LOADING)
  const [result, setResult] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  // Redirect to sign-in if not authenticated, preserving the destination
  useEffect(() => {
    if (!authLoading && !isLoggedIn) {
      navigate(`/sign-in?next=/a/${token}`, { replace: true })
    }
  }, [authLoading, isLoggedIn, navigate, token])

  // Redeem the token once we know the user is logged in
  useEffect(() => {
    if (authLoading || !isLoggedIn || !token) return

    let cancelled = false

    async function redeem() {
      try {
        const response = await redeemTokenOnce(token)
        if (cancelled) return

        if (response.success) {
          setResult(response.data)
          setStatus(STATUS.SUCCESS)
          return
        }

        setErrorMessage(getErrorMessage(response.errorCode, response.message))
        setStatus(STATUS.ERROR)
      } catch {
        if (cancelled) return

        setErrorMessage(getErrorMessage('REQUEST_FAILED'))
        setStatus(STATUS.ERROR)
      }
    }

    redeem()
    return () => {
      cancelled = true
    }
  }, [authLoading, isLoggedIn, token])

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              {status === STATUS.LOADING && <LoadingState />}
              {status === STATUS.SUCCESS && (
                <SuccessState
                  actionType={result?.actionType}
                  matchId={result?.matchId}
                />
              )}
              {status === STATUS.ERROR && <ErrorState message={errorMessage} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SmsActionPage
